import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { outcomeToStatusFilter } from '@flakiness/server/common/fql/extractor.js';
import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { humanReadableBytes, humanReadableInteger } from '@flakiness/shared/common/utils.js';
import { renderLastUpload } from '../fragments.js';
import { PageProjectTests } from '../page-project-tests.js';
import { PageProjectUsage } from '../page-project-usage.js';
import { linkStyles } from './cssstyles.js';

@customElement('list-projects-item')
export class ListProjectsItem extends LitElement {
  @property({ attribute: false }) project?: WireTypes.Project;
  @property({ type: Boolean }) showOrg = false;

  override render() {
    if (!this.project)
      return nothing;
    const project = this.project;
    const snapshot = project.snapshot;
    const outcomes = snapshot?.defaultReport.testOutcomes;
    const totalBytes = (snapshot?.reportBytes ?? 0) + (snapshot?.attachmentBytes ?? 0);
    return html`
      <h-box class=row>
        <sl-tooltip content=${`Run uploads in the last 30 days`}>
          <sparkline-chart
            .timezone=${project.timezone}
            .metrics=${snapshot?.sparkline ?? []}
            .days=${30}
          ></sparkline-chart>
        </sl-tooltip>
        <v-box style="gap: var(--sl-spacing-x-small)">
          <h-box>
            <a class=header href=${PageProjectTests.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId: undefined })}>${this.showOrg ? `${project.org.orgSlug}/${project.projectSlug}` : project.projectSlug}</a>
            <sl-tag pill size=small>${
              project.visibility === 'private' ? 'Private' :
              'Public'
            }</sl-tag>
          </h-box>
          <h-box class=description>
            <h-box style="gap: var(--sl-spacing-2x-small)">
              <sl-icon name=play-circle></sl-icon>
              <a href=${PageProjectUsage.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId: undefined })}>
                ${humanReadableInteger(snapshot?.runsCount ?? 0) + ' runs'}
              </a>
            </h-box>
            <span>•</span>
            <h-box style="gap: var(--sl-spacing-2x-small)">
              <sl-icon name=list-check></sl-icon>
              <a href=${PageProjectUsage.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId: undefined })}>
                ${humanReadableInteger(snapshot?.testRunsCount ?? 0) + ' test runs'}
              </a>
            </h-box>
            <span>•</span>
            <h-box style="gap: var(--sl-spacing-2x-small)">
              <sl-icon name=floppy></sl-icon>
              <a href=${PageProjectUsage.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId: undefined })}>
                ${humanReadableBytes(totalBytes)}
              </a>
            </h-box>
            <span>•</span>
            <h-box style="gap: var(--sl-spacing-2x-small)">
              ${renderLastUpload(project.lastUploadTimestamp)}
            </h-box>
          </h-box>
        </v-box>
        ${outcomes && !TestOutcomes.isEmptyOutcomeCounts(outcomes) ? html`
          <outcomes-bar
            .counts=${outcomes}
            .linkifier=${(outcome: WireTypes.Outcome) => PageProjectTests.url({
              orgSlug: project.org.orgSlug,
              projectSlug: project.projectSlug,
              query: `status:${outcomeToStatusFilter(outcome)}`,
              snapshotId: undefined,
            })}
          ></outcomes-bar>
        ` : html`<span class=no-tests>No tests</span>`}
      </h-box>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: grid;
      grid-template-columns: subgrid;
      grid-column: 1 / -1;
      min-width: 0;
    }

    .row {
      display: grid;
      grid-template-columns: subgrid;
      grid-column: 1 / -1;
      gap: var(--sl-spacing-large);
    }

    sparkline-chart {
      width: 120px;
      height: 25px;
    }

    .header {
      font-weight: var(--sl-font-weight-semibold);
    }

    .no-tests {
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);
      text-align: center;
    }

    .description {
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);
      white-space: nowrap;
      flex-wrap: wrap;
    }
  `];
}

@customElement('list-projects')
export class ListProjects extends LitElement {

  @property({ attribute: false }) projects?: WireTypes.Project[];
  // Prefix project names with their organization, for lists that span organizations.
  @property({ type: Boolean, attribute: 'show-org' }) showOrg = false;

  override render() {
    if (!this.projects || !this.projects.length)
      return nothing;
    const projects = this.projects.toSorted((p1, p2) => (p2.lastUploadTimestamp ?? 0) - (p1.lastUploadTimestamp ?? 0));
    return html`
      <section>
        ${projects.map(project => html`<list-projects-item .project=${project} .showOrg=${this.showOrg}></list-projects-item>`)}
      </section>
    `;
  }

  static styles = [linkStyles, css`
    section {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      grid-template-rows: 1fr;
      column-gap: var(--sl-spacing-large);
      border-radius: var(--sl-border-radius-medium);
      border: 1px solid var(--sl-panel-border-color);
    }

    list-projects-item + list-projects-item {
      border-top: 1px solid var(--sl-panel-border-color);
    }
    list-projects-item {
      padding: var(--sl-spacing-small);
    }
    list-projects-item:hover {
      background-color: var(--fk-color-tablerow-hover);
    }

    .no-items {
      color: var(--sl-color-neutral-400);
      font-size: var(--sl-font-size-medium);
    }
  `]
}
